import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatBRL } from "@/lib/constants";
import { Trophy, Zap, Star, MapPin, Medal, Flame, Crown, Share2 } from "lucide-react";
import ShareCard from "@/components/ShareCard";
import { logger } from "@/lib/logger";

const PODIUM_COLORS = [
  "bg-amber-500/20 text-amber-500 border-amber-500/40",   // 1º ouro
  "bg-zinc-400/20 text-zinc-300 border-zinc-400/40",     // 2º prata
  "bg-orange-600/20 text-orange-500 border-orange-600/40", // 3º bronze
];

const podiumColor = (idx) =>
  PODIUM_COLORS[idx] || "bg-secondary text-muted-foreground border-border";

const formatResponse = (sec) => {
  if (sec == null) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  return `${Math.round(sec / 60)}min`;
};

export default function Leaderboard() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareRow, setShareRow] = useState(null);
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leaderboard/weekly");
        setRows(data);
      } catch (err) {
        logger.error("leaderboard: failed to load", err);
      }
      setLoading(false);
    })();
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24" data-testid="leaderboard-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
          <Trophy size={22} strokeWidth={2.5} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-primary font-bold">Ranking semanal</p>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">
            Top Vendedores
          </h1>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        Os 10 vendedores que mais fecharam pedidos nos últimos 7 dias. Renova toda semana. 🔥
      </p>

      {loading ? (
        <p className="text-muted-foreground text-center py-12">Carregando ranking...</p>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-xl">
          <p className="text-muted-foreground">
            Ainda ninguém no ranking desta semana. Seja o primeiro! ⚡
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((row, idx) => (
            <div
              key={row.user_id}
              data-testid={`lb-row-${idx}`}
              className={`bg-card border border-border rounded-xl p-4 flex items-center gap-4 ${
                idx < 3 ? "ring-1 ring-primary/20" : ""
              }`}
            >
              <Link
                to={`/u/${row.user_id}`}
                className={`w-12 h-12 rounded-xl border-2 flex items-center justify-center font-heading font-black text-xl shrink-0 hover:scale-105 transition-transform ${podiumColor(idx)}`}
                data-testid={`lb-avatar-${idx}`}
              >
                {idx < 3 ? <Medal size={22} /> : idx + 1}
              </Link>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link
                    to={`/u/${row.user_id}`}
                    className="font-heading font-bold text-lg truncate hover:text-primary"
                    data-testid={`lb-name-${idx}`}
                  >
                    {row.name}
                  </Link>
                  {row.is_fast_seller && (
                    <span
                      data-testid={`fast-badge-${row.user_id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-primary/20 text-primary border border-primary/30 px-2 py-0.5 rounded-full"
                      title={`Responde em ~${formatResponse(row.avg_response_seconds)}`}
                    >
                      <Zap size={10} strokeWidth={3} /> Vendedor Rápido
                    </span>
                  )}
                  {row.top10_streak > 1 && (
                    <span
                      data-testid={`top10-streak-${row.user_id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-primary/10 text-primary border border-primary/30 px-2 py-0.5 rounded-full"
                      title={`${row.top10_streak} semanas seguidas no top 10`}
                    >
                      <Crown size={10} strokeWidth={3} /> {row.top10_streak}× top10
                    </span>
                  )}
                  {row.fast_accepts_this_week >= 3 && (
                    <span
                      data-testid={`fire-streak-${row.user_id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-orange-500/20 text-orange-400 border border-orange-500/30 px-2 py-0.5 rounded-full"
                      title={`${row.fast_accepts_this_week} pedidos aceitos em <2min esta semana`}
                    >
                      <Flame size={10} strokeWidth={3} /> {row.fast_accepts_this_week}×🔥
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                  <span className="flex items-center gap-1">
                    <Star size={11} className="text-amber-500 fill-amber-500" />
                    {row.rating_avg > 0 ? row.rating_avg.toFixed(1) : "—"} ({row.rating_count})
                  </span>
                  {row.city && (
                    <span className="flex items-center gap-1">
                      <MapPin size={11} /> {row.city}
                    </span>
                  )}
                  {row.avg_response_seconds != null && (
                    <span className="flex items-center gap-1 text-primary">
                      <Zap size={11} /> {formatResponse(row.avg_response_seconds)}
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0 flex items-center gap-2">
                <div>
                  <p className="font-heading font-black text-2xl text-primary tabular-nums">
                    {row.weekly_sales}
                  </p>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    vendas / 7d
                  </p>
                </div>
                {user?.user_id === row.user_id && (
                  <button
                    onClick={() => setShareRow({ ...row, weekly_position: idx + 1 })}
                    data-testid={`lb-share-${idx}`}
                    className="p-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90"
                    title="Compartilhar meu ranking"
                  >
                    <Share2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {shareRow && (
        <ShareCard
          open={!!shareRow}
          onClose={() => setShareRow(null)}
          user={shareRow}
          appUrl={appUrl}
        />
      )}
    </div>
  );
}
